// WHAT: Quick Stats Grid Component (Molecule)
// WHY:  Displays quick statistics in a compact grid layout
// DOMAIN: Productivity → Miracle Flow
// DEPENDENCIES: UI Card, Stack components, Lucide icons
// RISKS:
//   - NOTE: nextMilestone and sessionsUntilMilestone are optional (uses 0 as fallback)
//   - NOTE: Stats array is hardcoded (not dynamic based on available data)

import { Calendar, BarChart3, Clock, Target } from "lucide-react";
import React from "react";

import { Card, Stack } from "@/components/ui";
import { cn } from "@/shared/utils";

interface QuickStatsGridProperties {
  className?: string | undefined;
  nextMilestone?: number | undefined;
  sessionsUntilMilestone?: number | undefined;
  thisWeekSessions: number;
  totalSessions: number;
}

/**
 * WHAT:
 *   Grid component displaying quick statistics in compact card format.
 *
 * WHY:
 *   Provides at-a-glance view of key metrics (sessions, milestones) with visual icons.
 *
 * PROPS:
 *   - totalSessions: number — Total completed sessions
 *   - thisWeekSessions: number — Sessions completed this week
 *   - nextMilestone?: number — Next milestone target (optional)
 *   - sessionsUntilMilestone?: number — Sessions remaining to milestone (optional)
 *   - className?: string — Additional CSS classes
 *
 * BEHAVIOR:
 *   - Displays 4 stat cards in responsive grid (2 cols mobile, 4 cols desktop)
 *   - Each card shows icon, label, and value
 *   - Uses gradient backgrounds with TracQ branding colors
 *   - Cards have hover effects (border color, shadow, translate)
 *   - Optional milestone stats use 0 as fallback if not provided
 *
 * RISKS:
 *   - NOTE: Milestone stats are optional - shows 0 if not provided (may be misleading)
 *   - NOTE: Stats array is hardcoded (not configurable)
 *   - NOTE: Gradient classes are hardcoded strings (not from design tokens)
 *
 * @param root0
 *
 * @param root0.totalSessions
 *
 * @param root0.thisWeekSessions
 *
 * @param root0.nextMilestone
 *
 * @param root0.sessionsUntilMilestone
 *
 * @param root0.className
 *
 * @returns JSX.Element
 *
 */
export const QuickStatsGrid: React.FC<QuickStatsGridProperties> = ({
  className,
  nextMilestone,
  sessionsUntilMilestone,
  thisWeekSessions,
  totalSessions,
}) => {
  const stats = [
    {
      gradient: "from-tracq-deep-blue to-tracq-bright-blue",
      icon: BarChart3,
      label: "Total Sessions",
      value: totalSessions,
    },
    {
      gradient: "from-tracq-bright-blue to-tracq-deep-blue",
      icon: Calendar,
      label: "This Week",
      value: thisWeekSessions,
    },
    {
      gradient: "from-tracq-deep-orange to-tracq-light-orange",
      icon: Target,
      label: "Next Milestone",
      value: nextMilestone ?? 0,
    },
    {
      gradient: "from-tracq-light-orange to-tracq-deep-orange",
      icon: Clock,
      label: "Sessions Left",
      value: sessionsUntilMilestone ?? 0,
    },
  ];

  return (
    <div className={cn("grid grid-cols-2 md:grid-cols-4 gap-4", className)}>
      {stats.map((stat) => {
        const Icon = stat.icon;
        return (
          <Card
            className={cn(
              "bg-[#1E293B] border-[#334155]",
              "hover:border-tracq-bright-blue/50 transition-all duration-200",
              "hover:shadow-tracq-card hover:-translate-y-0.5",
            )}
            key={stat.label}
            padding="md"
            variant="default"
          >
            <Stack direction="col" gap={2}>
              <Stack align="center" direction="row" gap={2}>
                <div
                  className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-tracq shadow-tracq-card",
                    `bg-gradient-to-br ${stat.gradient}`,
                  )}
                >
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <span className="text-[#94A3B8] text-xs font-medium">{stat.label}</span>
              </Stack>
              <div className="text-2xl font-bold text-[#F1F5F9]">{stat.value}</div>
            </Stack>
          </Card>
        );
      })}
    </div>
  );
};
